import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { mainSiteUrl } from "@/mocks/kakobuy";
import { useSEO, SITE_URL } from "@/hooks/useSEO";
import type { ArticleData } from "@/mocks/articles";

function getArticleImageUrl(seq: string): string {
  const images: Record<string, string> = {
    "art-what-is": "https://readdy.ai/api/search-image?query=Modern%20organized%20spreadsheet%20on%20laptop%20screen%20with%20shopping%20products%20arranged%20around%20the%20workspace%20including%20sneakers%20hoodies%20and%20accessories%20on%20warm%20walnut%20wood%20desk%20with%20soft%20morning%20sunlight%20streaming%20through%20window%20creating%20gentle%20shadows%20clean%20professional%20aesthetic%20natural%20warm%20tones&width=1200&height=600&seq=art-what-is-2026-jun&orientation=landscape",
    "art-how-to-use": "https://readdy.ai/api/search-image?query=Step%20by%20step%20tutorial%20scene%20showing%20hands%20typing%20on%20laptop%20with%20colorful%20sticky%20notes%20and%20product%20photos%20spread%20across%20a%20bright%20oak%20desk%20with%20coffee%20cup%20and%20natural%20daylight%20from%20large%20window%20modern%20home%20office%20setting%20warm%20inviting%20atmosphere&width=1200&height=600&seq=art-how-to-use-2026-jun&orientation=landscape",
    "art-best-2026": "https://readdy.ai/api/search-image?query=Golden%20trophy%20and%20star%20rating%20symbols%20floating%20above%20an%20organized%20grid%20of%20fashion%20products%20including%20sneakers%20jackets%20and%20bags%20on%20a%20sophisticated%20dark%20slate%20surface%20with%20dramatic%20spotlight%20lighting%20premium%20editorial%20style&width=1200&height=600&seq=art-best-2026-jun&orientation=landscape",
    "art-poland": "https://readdy.ai/api/search-image?query=Polish%20flag%20colors%20subtly%20integrated%20into%20a%20fashion%20shopping%20scene%20with%20packages%20and%20products%20arranged%20on%20a%20warm%20amber%20toned%20surface%20map%20silhouette%20of%20Poland%20softly%20visible%20in%20the%20background%20clean%20editorial%20composition&width=1200&height=600&seq=art-poland-2026-jun&orientation=landscape",
    "art-safety": "https://readdy.ai/api/search-image?query=Security%20shield%20and%20padlock%20symbols%20floating%20above%20online%20shopping%20products%20on%20a%20serene%20mint%20green%20background%20with%20soft%20protective%20glow%20effect%20representing%20digital%20safety%20and%20buyer%20protection%20clean%20modern%20illustration&width=1200&height=600&seq=art-safety-2026-jun&orientation=landscape",
    "art-vs-allchina": "https://readdy.ai/api/search-image?query=Split%20screen%20comparison%20concept%20showing%20two%20distinct%20shopping%20carts%20side%20by%20side%20with%20contrasting%20product%20arrangements%20left%20side%20Kakobuy%20products%20in%20warm%20golden%20tones%20right%20side%20AllChinaBuy%20products%20in%20cool%20silver%20tones&width=1200&height=600&seq=art-vs-allchina-2026-jun&orientation=landscape",
    "art-vs-orientdig": "https://readdy.ai/api/search-image?query=Two%20distinct%20product%20collections%20displayed%20on%20separate%20pedestals%20under%20theatrical%20spotlighting%20one%20collection%20in%20earthy%20terracotta%20tones%20the%20other%20in%20oceanic%20teal%20tones%20representing%20different%20shopping%20platforms&width=1200&height=600&seq=art-vs-orientdig-2026-jun&orientation=landscape",
    "art-vs-superbuy": "https://readdy.ai/api/search-image?query=Epic%20versus%20battle%20concept%20with%20two%20towering%20stacks%20of%20fashion%20products%20facing%20each%20other%20like%20competitors%20on%20opposite%20sides%20of%20a%20polished%20arena%20floor%20overhead%20dramatic%20lighting%20casting%20long%20shadows&width=1200&height=600&seq=art-vs-superbuy-2026-jun&orientation=landscape",
    "art-best-agent": "https://readdy.ai/api/search-image?query=Podium%20with%20first%20second%20and%20third%20place%20positions%20featuring%20shopping%20related%20products%20on%20each%20tier%20surrounded%20by%20confetti%20and%20celebration%20elements%20on%20a%20sophisticated%20champagne%20gold%20background&width=1200&height=600&seq=art-best-agent-2026-jun&orientation=landscape",
  };
  return images[seq] || images["art-what-is"];
}

function buildArticleJsonLd(article: ArticleData) {
  const articleUrl = `${SITE_URL}/artykul/${article.slug}`;
  const articleSchema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.description,
    author: { "@type": "Organization", name: "kakobuy.one" },
    publisher: { "@type": "Organization", name: "kakobuy.one", url: SITE_URL },
    datePublished: "2026-06-21",
    dateModified: "2026-06-21",
    mainEntityOfPage: { "@type": "WebPage", "@id": articleUrl },
    url: articleUrl,
    inLanguage: "pl-PL",
  };

  const schemas: Record<string, unknown>[] = [articleSchema];

  if (article.faqs.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: article.faqs.map((faq) => ({
        "@type": "Question",
        name: faq.q,
        acceptedAnswer: { "@type": "Answer", text: faq.a },
      })),
    });
  }

  return schemas;
}

export default function ArticleTemplate({ article }: { article: ArticleData }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useSEO({
    title: article.metaTitle,
    description: article.metaDescription,
    keywords: "kakobuy spreadsheet, kakobuy, " + article.title.split(" ").slice(0, 5).join(" "),
    canonicalPath: `/artykul/${article.slug}`,
    ogTitle: article.metaTitle,
    ogDescription: article.metaDescription,
    ogType: "article",
    ogLocale: "pl_PL",
    jsonLd: buildArticleJsonLd(article),
  });

  return (
    <>
      <Navbar />
      <main className="pt-24 pb-16 bg-background-50">
        <article className="w-full max-w-4xl mx-auto px-4 md:px-6">
          <nav className="mb-8" aria-label="Breadcrumb">
            <ol className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
              <li>
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
                  Strona główna
                </Link>
              </li>
              <li className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-[10px]"></i>
              </li>
              <li className="text-foreground-700 font-medium">{article.category}</li>
              <li className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-[10px]"></i>
              </li>
              <li className="text-foreground-900 font-medium truncate max-w-[200px]">{article.title}</li>
            </ol>
          </nav>

          <header className="mb-8">
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                <span className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-price-tag-3-line text-[10px]"></i>
                </span>
                {article.category}
              </span>
              <span className="text-xs text-foreground-500">{article.readingTime} czytania</span>
            </div>

            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>

            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
              {article.description}
            </p>

            <div className="relative rounded-2xl overflow-hidden h-[200px] md:h-[320px] mt-8 mb-4">
              <img
                src={getArticleImageUrl(article.imageSeq)}
                alt={article.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/15 via-transparent to-transparent"></div>
            </div>

            <div className="flex items-center gap-4 pt-4 border-t border-background-200/70">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                  <span className="text-accent-600 font-semibold text-sm">R</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground-800">Redakcja kakobuy.one</p>
                  <p className="text-xs text-foreground-500">Ostatnia aktualizacja: 21 czerwca 2026</p>
                </div>
              </div>
            </div>
          </header>

          <div className="bg-background-100 rounded-2xl p-6 md:p-8 mb-10 border border-background-200/60">
            <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">Spis treści</h2>
            <ol className="space-y-2">
              {article.sections.map((section, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="text-primary-500 font-semibold text-sm min-w-[24px]">{i + 1}.</span>
                  <a
                    href={`#section-${i + 1}`}
                    className="text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    {section.heading}
                  </a>
                </li>
              ))}
            </ol>
          </div>

          {article.sections.map((section, i) => (
            <section key={i} id={`section-${i + 1}`} className="mb-10 scroll-mt-24">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                {i + 1}. {section.heading}
              </h2>
              {section.content.map((paragraph, j) => (
                <p key={j} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  {paragraph}
                </p>
              ))}
            </section>
          ))}

          {article.faqs.length > 0 && (
            <section className="mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                FAQ — Najczęściej zadawane pytania
              </h2>
              <div className="space-y-4">
                {article.faqs.map((faq, i) => (
                  <details key={i} className="group bg-background-100 rounded-xl border border-background-200/60">
                    <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                      <span className="text-sm md:text-base font-semibold text-foreground-800 pr-4">{faq.q}</span>
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 group-open:rotate-180 transition-transform duration-200">
                        <i className="ri-arrow-down-s-line text-foreground-500"></i>
                      </span>
                    </summary>
                    <div className="px-5 pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </section>
          )}

          <div className="flex flex-col sm:flex-row items-center gap-4 p-6 bg-primary-50 rounded-2xl border border-primary-200 mt-10">
            <div className="text-center sm:text-left">
              <p className="text-base font-semibold text-primary-800 mb-1">
                Gotowy, aby odkryć najlepsze okazje?
              </p>
              <p className="text-sm text-primary-600">
                Sprawdź kakobuy spreadsheet już teraz i znajdź produkty idealne dla siebie.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-2.5 bg-primary-500 text-white font-semibold text-sm rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                buy it now
              </a>
              <Link
                to="/"
                className="px-6 py-2.5 border border-primary-300 text-primary-700 font-medium text-sm rounded-full hover:bg-primary-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                Wróć na stronę główną
              </Link>
            </div>
          </div>

          <div className="text-center pt-8 border-t border-background-200/70 mt-8">
            <p className="text-xs text-foreground-400 mb-2">Ten artykuł ma charakter wyłącznie informacyjny.</p>
            <p className="text-xs text-foreground-400">
              © 2026 kakobuy.one — Wszelkie prawa zastrzeżone.{" "}
              <Link to="/" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">
                Powrót do strony głównej
              </Link>
            </p>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}